import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { SectionTitle, KpiCard } from "@/components/dashboard/widgets";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Headset, Database, Send, Globe2, Boxes, HelpCircle, Eye, Mail, Megaphone, FileBarChart2,
  CheckCircle2, PhoneCall, Archive, Inbox as InboxIcon,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { NurturePipeline } from "@/pages/admin/NurturePipeline";
import { RowBin, ClearArea } from "@/components/admin/Purge";

const STATUS_ORDER = ["all", "new", "contacted", "closed"];
const STATUS_HUE = {
  new: "bg-blue-50 text-blue-700 ring-blue-200",
  contacted: "bg-amber-50 text-amber-700 ring-amber-200",
  closed: "bg-slate-100 text-slate-600 ring-slate-200",
};
const SERVICE_ICON = {
  "BPO & Managed Operations": Headset,
  "Data Provision & Enrichment": Database,
  "SMS & Email Solutions": Send,
  "Web Development": Globe2,
  "System Builds": Boxes,
  "Paid Media & Creative": Megaphone,
  "CPL Benchmark Report": FileBarChart2,
};

function StatusPill({ status }) {
  return (
    <span className={`text-[10.5px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full ring-1 ${STATUS_HUE[status] || STATUS_HUE.closed}`}>
      {status}
    </span>
  );
}

export default function Enquiries() {
  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [filter, setFilter] = useState("new");
  const [serviceFilter, setServiceFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);
  const [note, setNote] = useState("");
  const [tab, setTab] = useState("enquiries");

  const refresh = async () => {
    setLoading(true);
    try {
      const [r, all] = await Promise.all([
        api.get(`/admin/enquiries?status=${filter}`),
        api.get("/admin/enquiries?status=all&limit=500"),
      ]);
      setRows(r.data || []);
      setAllRows(all.data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [filter]);

  const counts = useMemo(() => {
    const c = { new: 0, contacted: 0, closed: 0 };
    allRows.forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [allRows]);

  const services = useMemo(
    () => [...new Set(allRows.map((r) => r.service).filter(Boolean))].sort(),
    [allRows]
  );

  const visible = serviceFilter === "all" ? rows : rows.filter((r) => r.service === serviceFilter);

  const setStatus = async (id, status, withNote = false) => {
    try {
      const payload = withNote && note.trim() ? { status, note: note.trim() } : { status };
      await api.patch(`/admin/enquiries/${id}`, payload);
      toast.success(`Marked as ${status}.`);
      setNote("");
      refresh();
      if (viewing?.id === id) setViewing({ ...viewing, status });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update enquiry");
    }
  };

  return (
    <div className="space-y-6">
      <SectionTitle
        title="Service enquiries"
        subtitle="Managed-services enquiries and benchmark report downloads — plus the automated nurture pipeline."
      />

      <div className="flex items-center gap-1 border-b border-slate-200">
        {[["enquiries", "Enquiries"], ["nurture", "Nurture pipeline"]].map(([k, label]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            data-testid={`enq-tab-${k}`}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === k ? "border-[#0055FF] text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "nurture" && <NurturePipeline />}

      {tab === "enquiries" && (<>
      <div className="grid sm:grid-cols-4 gap-4">
        <KpiCard label="New" value={counts.new} testid="enq-kpi-new" />
        <KpiCard label="Contacted" value={counts.contacted} testid="enq-kpi-contacted" />
        <KpiCard label="Closed" value={counts.closed} testid="enq-kpi-closed" />
        <KpiCard label="Total" value={allRows.length} testid="enq-kpi-total" />
      </div>

      <div className="flex items-center gap-1.5 flex-wrap">
        {STATUS_ORDER.map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            data-testid={`enq-filter-${s}`}
            className={`rounded-full text-xs font-medium px-3 py-1.5 border transition-all capitalize ${
              filter === s ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"
            }`}
          >
            {s}
          </button>
        ))}
        {services.length > 0 && (
          <select
            data-testid="enq-service-filter"
            value={serviceFilter}
            onChange={(e) => setServiceFilter(e.target.value)}
            className="ml-auto h-8 rounded-full border border-slate-200 bg-white px-3 text-xs font-medium text-slate-700 focus:outline-none"
          >
            <option value="all">All services</option>
            {services.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        )}
        <div className={services.length > 0 ? "" : "ml-auto"}>
          <ClearArea area="enquiries" onDone={refresh} label="Clear enquiries" />
        </div>
      </div>

      <Card className="rounded-xl shadow-soft border-slate-200">
        <CardContent className="p-0">
          {loading && <div className="p-6 text-sm text-slate-500">Loading…</div>}
          {!loading && visible.length === 0 && (
            <div className="p-10 text-center" data-testid="enq-empty">
              <InboxIcon className="size-8 text-slate-300 mx-auto" />
              <p className="mt-3 text-sm text-slate-500">No enquiries in this filter.</p>
            </div>
          )}
          {!loading && visible.length > 0 && (
            <ul className="divide-y divide-slate-100">
              {visible.map((r) => {
                const Icon = SERVICE_ICON[r.service] || HelpCircle;
                return (
                  <li key={r.id} className="p-5 hover:bg-slate-50/60 transition-colors" data-testid={`enq-row-${r.id}`}>
                    <div className="flex items-start gap-3">
                      <span className="inline-grid place-items-center size-8 rounded-md bg-slate-900 text-white shrink-0">
                        <Icon className="size-4" strokeWidth={1.6} />
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-slate-900">{r.name}</span>
                          <StatusPill status={r.status} />
                          <a href={`mailto:${r.email}`} className="text-[11px] font-mono text-slate-500 hover:text-primary">{r.email}</a>
                          {r.company && (
                            <>
                              <span className="text-[11px] text-slate-400">·</span>
                              <span className="text-[11px] text-slate-500">{r.company}</span>
                            </>
                          )}
                          <span className="text-[11px] text-slate-400 ml-auto font-mono tabular-nums">
                            {new Date(r.created_at).toLocaleString("en-GB")}
                          </span>
                        </div>
                        <div className="mt-1.5 text-[11px] font-semibold uppercase tracking-widest text-[#0055FF]">{r.service}</div>
                        <p className="mt-1.5 text-sm text-slate-700 leading-relaxed line-clamp-2">{r.message}</p>
                      </div>
                      <div className="shrink-0 flex flex-col gap-1.5">
                        <Button variant="outline" size="sm" className="rounded-full h-7 px-3 text-[11px]" onClick={() => { setViewing(r); setNote(r.note || ""); }} data-testid={`enq-view-${r.id}`}>
                          <Eye className="size-3 mr-0.5" /> View
                        </Button>
                        {r.status === "new" && (
                          <Button variant="outline" size="sm" className="rounded-full h-7 px-3 text-[11px]" onClick={() => setStatus(r.id, "contacted")} data-testid={`enq-contacted-${r.id}`}>
                            <PhoneCall className="size-3 mr-0.5 text-amber-600" /> Contacted
                          </Button>
                        )}
                        {r.status !== "closed" && (
                          <Button variant="ghost" size="sm" className="rounded-full h-7 px-3 text-[11px]" onClick={() => setStatus(r.id, "closed")} data-testid={`enq-close-${r.id}`}>
                            <Archive className="size-3 mr-0.5" /> Close
                          </Button>
                        )}
                        <div className="self-end"><RowBin area="enquiries" id={r.id} name={r.name} onDone={refresh} /></div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
      </>)}

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="sm:max-w-[640px]" data-testid="enq-detail-dialog">
          {viewing && (
            <>
              <DialogHeader>
                <DialogTitle>{viewing.service} — {viewing.name}</DialogTitle>
                <DialogDescription className="font-mono text-[11px]">{viewing.email}{viewing.company ? ` · ${viewing.company}` : ""}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-slate-900 leading-relaxed whitespace-pre-wrap">{viewing.message}</p>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-[11.5px] font-mono space-y-1">
                  <div className="flex justify-between"><span className="text-slate-500">Received</span><span>{new Date(viewing.created_at).toLocaleString("en-GB")}</span></div>
                  {viewing.phone && <div className="flex justify-between"><span className="text-slate-500">Phone</span><span>{viewing.phone}</span></div>}
                  {viewing.handled_by && <div className="flex justify-between"><span className="text-slate-500">Handled by</span><span>{viewing.handled_by}</span></div>}
                  {viewing.updated_at && <div className="flex justify-between"><span className="text-slate-500">Last update</span><span>{new Date(viewing.updated_at).toLocaleString("en-GB")}</span></div>}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="enq-note" className="text-xs font-medium">Internal note (saved with status changes)</Label>
                  <Textarea
                    id="enq-note"
                    data-testid="enq-note"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    rows={3}
                    placeholder="e.g. Called 14 Jun — wants a scope call for a broker portal build, budget ~£15k."
                    maxLength={2000}
                  />
                </div>
                <div className="flex items-center gap-2 pt-2 border-t border-slate-100 flex-wrap">
                  <Badge variant="outline" className="rounded-full">Status: <span className="ml-1 capitalize">{viewing.status}</span></Badge>
                  <div className="ml-auto flex gap-1.5 flex-wrap">
                    <a href={`mailto:${viewing.email}?subject=${encodeURIComponent(`Re: your Connexis enquiry — ${viewing.service}`)}`}>
                      <Button variant="outline" size="sm" className="rounded-full" data-testid="enq-reply-email">
                        <Mail className="size-3.5 mr-1" /> Reply by email
                      </Button>
                    </a>
                    <Button variant="outline" size="sm" onClick={() => setStatus(viewing.id, "contacted", true)} className="rounded-full" data-testid="enq-detail-contacted">
                      <PhoneCall className="size-3.5 mr-1 text-amber-600" /> Contacted
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setStatus(viewing.id, "closed", true)} className="rounded-full" data-testid="enq-detail-close">
                      <CheckCircle2 className="size-3.5 mr-1 text-emerald-600" /> Close
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
